import { twMerge } from "tailwind-merge";
import {
    Building2,
    CheckCircle,
    CircleGauge,
    CircleHelp,
    Columns2,
    Compass,
    ExternalLink,
    FileSearch,
    FlipHorizontal,
    House,
    Inbox,
    LayoutDashboard,
    MapPin,
    MessageSquareQuote,
    PackageSearch,
    Pickaxe,
    ReceiptText,
    ScanText,
    Settings,
    ShieldAlert,
    ShieldCheck,
    Users,
    X,
} from "lucide-react";
import { Link } from "react-router";
import SlideoutMenu from "../Menu/SlideoutMenu";
import { useContext, useRef } from "react";
import { LayoutContext } from "../../../context/LayoutContext";
import Button from "../../DesignSystem/Button";
import VendorDiscoveryIcon from "../../Common/CustomIcons/VendorDiscoveryIcon";
import { Input } from "../../DesignSystem";
import UserDropdown from "../Menu/UserDropdown";
import Tooltip from "../../DesignSystem/Tooltip";
import NotificationsPanel from "../../Notifications/NotificationsPanel";
import SandboxBadge from "./SandboxBadge";

/**
 * This component is used to add a header to the top of a page.  The header has 3 columns on desktop.  The middle column
 * shifts to line 2 on mobile.
 *
 * Styling is done automatically in certain instances based on the type of elements in each column.  The first column, for example, is traditionally
 * a breadcrumb trail so we place the breadcrumbs there if that prop value is set.
 */
export const ICON_KEYS = {
    Home: <House className="h-4 w-4 stroke-stone-400" />,
    Dashboard: <LayoutDashboard className="h-4 w-4 stroke-stone-400" />,
    DocumentHub: <Compass className="h-4 w-4 stroke-neutral-400" />,
    RFQ: <MessageSquareQuote className="h-4 w-4 stroke-stone-400" />,
    PurchaseOrder: <ReceiptText className="h-4 w-4 stroke-stone-400" />,
    Requisition: <Inbox className="h-4 w-4 stroke-stone-400" />,
    Vendors: <Building2 className="h-4 w-4 stroke-stone-400" />,
    Users: <Users className="h-4 w-4 stroke-stone-400" />,
    Address: <MapPin className="h-4 w-4 stroke-stone-400" />,
    Columns2: <Columns2 className="h-4 w-4 stroke-stone-400" />,
    ScanText: <ScanText className="h-4 w-4 stroke-stone-400" />,
    Pickaxe: <Pickaxe className="h-4 w-4 stroke-stone-400" />,
    IndustrialProducts: <PackageSearch className="h-4 w-4 stroke-stone-400" />,
    VendorDiscovery: <VendorDiscoveryIcon.Stone />,
    ContactsAndVendors: <Building2 className="h-4 w-4 stroke-stone-400" />,
    Settings: <Settings className="h-4 w-4 stroke-stone-400" />,
    SourcingRequest: <FileSearch className="h-4 w-4 stroke-stone-400" />,
    SpendAnalysis: <CircleGauge className="h-4 w-4 stroke-stone-400" />,
    Threats: <ShieldAlert className="h-4 w-4 stroke-stone-400" />,
    ComplianceAudit: <ShieldCheck className="h-4 w-4 stroke-stone-400" />,
    Compare: <FlipHorizontal className="h-4 w-4 stroke-stone-400" />,
};

export type HeaderIconKey = keyof typeof ICON_KEYS;

interface Breadcrumb {
    label?: string;
    link?: string;
    renameValue?: string;
    canRename?: boolean;
    handleRename?: (newName: string) => void;
    // Render a skeleton box instead of text — for a leaf crumb whose value is still loading.
    loading?: boolean;
}

// Subcomponent for renamable breadcrumb
function RenamableBreadcrumb({ crumb, index, isRenaming, setIsRenaming }) {
    const renameInputRef = useRef<HTMLInputElement | null>(null);

    const handleRenameSubmit = async () => {
        try {
            const newName = renameInputRef.current?.value;
            await crumb.handleRename?.(newName);
            setIsRenaming(false);
        } catch (error) {
            console.error("Error renaming breadcrumb:", error);
        }
    };

    const handleRenameKeyDown = e => {
        if (e.key === "Enter") {
            handleRenameSubmit();
        }
    };

    if (isRenaming) {
        return (
            <div className="flex items-center gap-2">
                <Input
                    id={`breadcrumb-${index}`}
                    ref={renameInputRef}
                    type="text"
                    defaultValue={crumb.renameValue ? crumb.renameValue : crumb.label}
                    className="min-w-[180px] bg-white sm:min-w-[300px]"
                    onKeyDown={handleRenameKeyDown}
                />
                <Button color="bronze" className="h-8" icon={<CheckCircle className="h-4 w-4 stroke-white" />} onClick={handleRenameSubmit} />
                <Button color="white" className="h-8" icon={<X className="h-4 w-4 stroke-stone-400" />} onClick={() => setIsRenaming(false)} />
            </div>
        );
    }
    return (
        <button className="text-stone-500 hover:text-stone-600" onClick={() => setIsRenaming(true)}>
            <span className="line-clamp-1 max-w-[200px] truncate text-ellipsis md:max-w-full">{crumb.label}</span>
        </button>
    );
}

const Breadcrumbs = ({ breadcrumbs }: { breadcrumbs: Breadcrumb[] }) => {
    const { isRenamingBreadcrumb, setIsRenamingBreadcrumb } = useContext(LayoutContext);

    // Helper to render a single breadcrumb
    const renderBreadcrumb = (crumb, index) => {
        if (crumb.loading) {
            return <div className="h-5 w-40 animate-pulse rounded-md bg-stone-200" />;
        }
        if (crumb.link) {
            return (
                <Link
                    key={crumb.label}
                    to={crumb.link}
                    className={`${index === breadcrumbs.length - 1 ? "text-stone-950" : "text-stone-600"} line-clamp-1 flex shrink-0 items-center gap-1.5 text-nowrap`}
                >
                    {crumb.label}
                </Link>
            );
        } else if (crumb.canRename) {
            return (
                <RenamableBreadcrumb
                    key={crumb.label}
                    crumb={crumb}
                    index={index}
                    isRenaming={isRenamingBreadcrumb}
                    setIsRenaming={setIsRenamingBreadcrumb}
                />
            );
        } else {
            return (
                <span
                    key={crumb.label}
                    className={`${index === breadcrumbs.length - 1 ? "text-stone-950" : "text-stone-600"} line-clamp-1 max-w-[200px] text-ellipsis md:max-w-full`}
                >
                    {crumb.label}
                </span>
            );
        }
    };

    // Render breadcrumbs with separators
    return (
        <div className="flex items-center gap-2 font-medium">
            {breadcrumbs.map((crumb, index) => (
                <div key={`${crumb.label ?? "crumb"}-${index}`} className="flex items-center gap-2">
                    {renderBreadcrumb(crumb, index)}
                    {index !== breadcrumbs.length - 1 && <span className="text-stone-300">/</span>}
                </div>
            ))}
        </div>
    );
};

const BetaBadge = () => (
    <div className="flex items-center pb-0.5">
        <div className="mr-1 rounded bg-linear-to-l from-stone-950 to-[#15803D] px-1.5 py-px text-xs font-semibold text-white">Beta</div>
        <div className="hidden text-xs sm:inline">Free to use while in beta</div>
    </div>
);

interface HeaderProps {
    hideNav?: boolean;
    iconKey?: HeaderIconKey;
    breadcrumbs?: Breadcrumb[]; // breadcrumbs take precedent over the left prop.  only one is rendered
    beta?: boolean; // if true, the beta badge should be shown
    left?: React.ReactNode; // left won't render if breadcrumbs is set.
    actions?: React.ReactNode;
    // Pages that opt out of the shell's gutters supply their own inset here.
    className?: string;
}

export default function Header({ actions, beta, breadcrumbs, hideNav, iconKey, left, className }: HeaderProps) {
    return (
        <div className={twMerge("sticky top-0 z-30 flex h-[48px] w-full items-center truncate bg-stone-100 sm:w-auto md:h-[56px]", className)}>
            <div>
                {/** Desktop Menu */}
                {!hideNav && (
                    <div className="mr-4 hidden items-center gap-2 md:flex">
                        <SlideoutMenu showFullMenu={!hideNav} />
                        <div className="h-6 w-px bg-stone-200" />
                    </div>
                )}

                {/** Mobile Menu */}
                <div className="mx-2 flex items-center gap-2 md:hidden">
                    <SlideoutMenu showFullMenu={!hideNav} />
                </div>
            </div>

            <div className="flex items-center gap-2">
                {iconKey && <div className="shrink-0">{ICON_KEYS[iconKey]}</div>}
                {breadcrumbs ? <Breadcrumbs breadcrumbs={breadcrumbs} /> : left}
            </div>

            <div className="mr-2 ml-auto flex items-center gap-2">
                {actions}
                {actions && <div className="ml-4 hidden h-6 w-px bg-stone-200 md:block" />}
                <SandboxBadge />
            </div>
            <Tooltip
                content={
                    <span className="flex items-center gap-1.5">
                        User Guides <ExternalLink className="h-3 w-3" />
                    </span>
                }
                align="bottom"
                childrenClassName="ml-auto md:ml-0 hidden md:block mr-2"
            >
                <Button
                    color="clear"
                    className="ml-auto hidden py-2 hover:bg-stone-200 active:bg-stone-300 md:block"
                    icon={<CircleHelp className="h-4 w-4 stroke-stone-600" />}
                    target="_blank"
                    href="https://docs.purchaser.ai/"
                />
            </Tooltip>
            <NotificationsPanel />
            <UserDropdown />
        </div>
    );
}
